import { Board } from '@common-models/board'
import { TaskColumn } from '@common-models/task-column'
import { getSelectedBoardId } from '@common-reducers/BoardsSelectors'
import { initialAutoMappingExcelToBoardColumnsThunks } from '@common-reducers/BoardsThunks'
import { boardsSelectors } from "@common-reducers/DBServiceThunks"
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook'
import { importItemsActions } from '@common-reducers/ImportItemsReducer'
import { modalActions } from '@common-reducers/ModalReducer'
import { dynamicColumnList } from '@common-reducers/TaskSelectors'
import { getRuntimeUrl } from '@common-services/storage'
import { waQueryContactsAndChatsTool } from '@common/tools/whatsapp'
import CircularProgressWithText from '@extension/context/content/components/bt-bulk-dashboard/audiences/circular-progress-with-text'
import { ContactTable } from '@extension/context/content/components/bulk-dashboard/contact-table/contact-table'
import React, { ForwardedRef, useEffect, useState } from 'react'
import { FaAngleRight } from 'react-icons/fa'
import { RiFileExcel2Line } from 'react-icons/ri'
import { ArrowRight } from 'lucide-react'
import FecthDataToImportDialog from './fetch-data-to-import-dialog'
import ImportNavButtons from './import-nav-buttons'
import MapImportedDataDialog from './map-imported-data-dialog'
import SelectImportTypeDialog from './select-import-type-dialog'
import { cn } from '@/lib/utils'

type Props = {
    handleFinishImporting?: (boardId?: string) => void;
}


export const supportedColumnTypes = ['text-cell', 'number', 'phone-cell', 'task name',
    'datepicker', 'whatsapp-id-cell', 'option-picker', 'status-option-picker'
]

const ImportExcelToBlueticks = React.forwardRef<HTMLDivElement, Props>(({
    handleFinishImporting,
}: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const dispatch = useAppDispatch()
    const selectedBoardId = useAppSelector((state) => getSelectedBoardId(state))
    const board: Board = useAppSelector((state) => boardsSelectors.selectById(state, selectedBoardId))
    const _columnsList = useAppSelector((state) => dynamicColumnList(state, selectedBoardId))
    const _importJsonData = useAppSelector((state) => state.ImportItemsReducer.importJsonData);
    const importFileName = useAppSelector((state) => state.ImportItemsReducer.importFileName);
    const selectedColumn = useAppSelector((state) => state.ImportItemsReducer.selectedColumn);
    const importInProgress = useAppSelector((state) => state.ImportItemsReducer.importInProgress);
    const openedby = useAppSelector((state) => state.ModalReducer.openedby)
    const importType = useAppSelector((state) => state.ModalReducer.importType)
    const step = useAppSelector((state) => state.ImportItemsReducer.step);


    const [importJsonData, setImportJsonData] = useState<unknown[]>([])

    const srcExcelImg = openedby === 'campaign' ? getRuntimeUrl("/img/excel.png") : "/img/excel.png"
    const srcBTLogoImg = openedby === 'campaign' ? getRuntimeUrl("/img/blueticks_logo_icon.png") : "/img/blueticks_logo_icon.png"

    let columnsList
    if (!selectedBoardId) {
        columnsList = [
            {
                _id: 'taskName',
                title: 'Display Name',
                type: 'task name',
            } as TaskColumn,
            {
                _id: 'fakeFirstNameColumnId012',
                title: 'first name',
                type: 'text-cell',
            } as TaskColumn,
            {
                _id: 'fakeLastNameColumnId0123',
                title: 'last name',
                type: 'text-cell',
            } as TaskColumn,
            {
                _id: 'fakeContactColumnId01234',
                title: 'phone',
                type: 'whatsapp-id-cell',
            } as TaskColumn,
        ]
    }
    else {
        columnsList = [..._columnsList, { _id: 'taskName', title: 'Task Name', type: 'task name' }]
    }


    const goNext = (isFromSheets?: boolean) => {
        if (importType === 'whatsapp') {
            dispatch(modalActions.setImportType('whatsappWithMapping'));
        }
        if (importFileName && (!selectedColumn || openedby === 'campaign') && step === 0) {
            dispatch(importItemsActions.setStep(2))
        } else if (importFileName && selectedColumn && step !== 0) {
            dispatch(importItemsActions.setStep(2))
        }
        else if (isFromSheets && step !== 2) {
            dispatch(importItemsActions.setStep(2))
        }
    }

    // Prefetch WhatsApp contacts when entering the WhatsApp step so the ContactTable
    // actually populates. Without this the list stays empty in this dialog because
    // ContactTable only kicks off campaigns/messages loading, not contacts.
    useEffect(() => {
        if (importType === 'whatsapp' || importType === 'whatsappWithMapping') {
            try {
                waQueryContactsAndChatsTool({})
            } catch (e) {
                console.error('[waQueryContactsAndChatsTool] error', e)
            }
        }
    }, [importType])


    const renderStepContent = () => {
        if (!importType) {
            return (
                <div key="step-select-type" className="flex flex-col h-full">
                    <SelectImportTypeDialog />
                </div>
            );
        }

        if (importType === 'whatsapp') {
            return (
                <div key="step-whatsapp-contacts" className="flex flex-col h-full min-h-0">
                    <div className="flex-1 min-h-0 flex flex-col">
                        <ContactTable />
                    </div>
                </div>
            );
        }

        if (importType === 'whatsappWithMapping') {
            return (
                <div key="step-whatsapp-mapping" className="flex flex-col h-full">
                    <div className="flex-1 flex flex-col min-h-0">
                        <MapImportedDataDialog
                            columnsList={columnsList?.filter(column => column._id !== 'taskName') ?? []}
                        />
                    </div>
                </div>
            );
        }

        if (step === 0 && importType && !['whatsapp', 'whatsappWithMapping'].includes(importType)) {
            return (
                <div key="step-upload-file" className="flex flex-col h-full">
                    {board?.name && board?.name !== '' && (
                        <div className="flex items-center mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                            <RiFileExcel2Line color='#437445' size={21} />
                            <FaAngleRight size={15} className="mx-1" />
                            <img src="/img/blueticks_logo_icon.png" className="h-5 mr-2" alt="" />
                            {board?.titleIcon}
                            <span className="font-normal ml-1">{board?.name}</span>
                        </div>
                    )}
                    <div className="flex-1 flex flex-col min-h-0">
                        <FecthDataToImportDialog goNext={goNext} />
                    </div>
                </div>
            );
        }

        if (step === 2) {
            return (
                <div key="step-map-columns" className="flex flex-col h-full">
                    {board?.name && board?.name !== '' && (
                        <div className="flex items-center mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                            <RiFileExcel2Line color='#437445' size={21} />
                            <FaAngleRight size={15} className="mx-1" />
                            <img src="/img/blueticks_logo_icon.png" className="h-5 mr-2" alt="" />
                            {board?.titleIcon}
                            <span className="font-normal ml-1">{board?.name}</span>
                        </div>
                    )}
                    <div className="flex-1 flex flex-col min-h-0">
                        <MapImportedDataDialog
                            importJsonData={importJsonData}
                            columnsList={columnsList ?? []}
                        />
                    </div>
                </div>
            );
        }

        return null;
    }

    // Single fixed footer hosts the nav buttons across every step so positions stay consistent.
    // Hidden on the initial method-picker (no importType) and on the final success step (step 3).
    const showFooter = !!importType && step !== 3;


    useEffect(() => {
        setImportJsonData(_importJsonData)

        dispatch(initialAutoMappingExcelToBoardColumnsThunks({
            columnsList,
        }))
    }, [_importJsonData, _columnsList, dispatch])


    useEffect(() => {
        dispatch(importItemsActions.setImportDataMapping({}))
        dispatch(importItemsActions.setNewColumnList([]))
    }, [dispatch])


    // Right decorative panel is part of the brand chrome for CSV/Sheets/method-picker.
    // Hidden on the WhatsApp contacts step so the contact list gets the full width.
    const showRightPanel = importType !== 'whatsapp' && step !== 3;

    return (
        <div
            ref={ref}
            className={cn(
                "h-full w-full max-w-[1024px] rounded-xl",
                "flex flex-col",
                showRightPanel && "lg:grid lg:grid-cols-[62%_38%]"
            )}
        >
            <div className={cn(
                "relative p-6 lg:p-8 bg-[var(--color-surface)] dark:bg-[var(--color-surface)]",
                showRightPanel ? "rounded-t-xl lg:rounded-l-xl lg:rounded-tr-none lg:rounded-br-none" : "rounded-xl",
                step === 3 && "rounded-xl",
                "h-full box-border flex-1 min-h-0 flex flex-col",
                showRightPanel && "lg:border-r lg:border-slate-200 lg:dark:border-slate-700",
                "overflow-hidden"
            )}>
                <div className="h-full flex flex-col min-h-0">
                    {renderStepContent()}
                </div>

                {showFooter && (
                    <div className="flex-shrink-0 pt-4 mt-4 border-t border-slate-200 dark:border-slate-700">
                        <ImportNavButtons
                            selectedBoardId={selectedBoardId}
                            importJsonData={importJsonData}
                            handleFinishImporting={handleFinishImporting}
                            goNext={goNext}
                        />
                    </div>
                )}

                {importInProgress && (
                    <div className="absolute inset-0 bg-[var(--color-surface)] dark:bg-[var(--color-surface)] flex flex-row items-center justify-center z-50 backdrop-blur-sm">
                        <CircularProgressWithText text={openedby === 'campaign' ? "Importing contacts…" : "Importing data…"} />
                    </div>
                )}
            </div>

            {showRightPanel && (
                <div className={cn(
                    "flex items-center justify-center",
                    "w-full lg:h-full",
                    "rounded-b-xl lg:rounded-b-none lg:rounded-r-xl",
                    "bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900",
                    "p-8 sm:p-10",
                    "mt-6 lg:mt-0"
                )}>
                    <div className={cn(
                        "relative flex items-center justify-center",
                        "h-[200px] w-[200px] sm:h-[235px] sm:w-[235px]",
                        "bg-[var(--tasks-group-bg)] dark:bg-[var(--tasks-group-bg)]",
                        "border-[3px] border-[var(--color-surface)] dark:border-[var(--color-surface)]",
                        "border-l-[#00800042] border-r-[var(--color-accent)]52 border-b-[var(--color-accent)] border-t-[#45821a]",
                        "rounded-full rotate-45"
                    )}>
                        <div className={cn(
                            "absolute h-[50px] w-[50px]",
                            "flex items-center justify-center",
                            "bg-white dark:bg-white",
                            "rounded-full",
                            "shadow-[0_1px_3px_0_var(--tasks-input-border)]",
                            "top-[73%] left-[2%]",
                            "border-[4px] border-[#4e7f380a]",
                            "rotate-[-45deg]"
                        )}>
                            <img src={srcExcelImg} className="h-10 w-10 object-contain" alt="Excel logo" />
                        </div>
                        <div className="rotate-[-45deg]">
                            <ArrowRight size={28} className="text-[var(--color-accent)]" />
                        </div>
                        <div className={cn(
                            "absolute top-[2%] right-[2%]",
                            "h-[50px] w-[50px]",
                            "flex items-center justify-center",
                            "rotate-[-45deg]"
                        )}>
                            <img src={srcBTLogoImg} className="h-10 w-10 object-contain" alt="Blueticks logo" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
})

export default ImportExcelToBlueticks
